import { useState, useCallback } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'

/** Minimum mastery % required to unlock a Sage subject certificate */
export const CERT_THRESHOLD = 90

export interface CertificateOptions {
  /** For Sage subject certificates */
  category_id?: string
  class_id?: string | null
  student_id?: string | null
  /** For University certificates */
  type?: 'sage' | 'university'
  degree_slug?: string
  module_id?: number | null
}

export interface UseCertificateReturn {
  isGenerating: boolean
  error: string | null
  certificateUrl: string | null
  generate: (opts: CertificateOptions) => Promise<string | null>
  download: (url: string, filename?: string) => void
  reset: () => void
}

/**
 * useCertificate
 * Calls the generate-certificate edge function with the current user's auth
 * token. On success returns (and caches) the certificate PDF URL.
 */
export function useCertificate(): UseCertificateReturn {
  const supabase = createClientComponentClient()
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [certificateUrl, setCertificateUrl] = useState<string | null>(null)

  const generate = useCallback(async (opts: CertificateOptions): Promise<string | null> => {
    setIsGenerating(true)
    setError(null)
    setCertificateUrl(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        setError('You must be signed in to generate a certificate.')
        return null
      }

      const body: Record<string, any> = { type: opts.type ?? 'sage' }
      if (opts.category_id) body.category_id = opts.category_id
      if (opts.class_id)    body.class_id    = opts.class_id
      if (opts.student_id) body.student_id  = opts.student_id
      if (opts.degree_slug) body.degree_slug = opts.degree_slug
      if (opts.module_id)   body.module_id   = opts.module_id

      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'generate-certificate',
        { body, headers: { Authorization: `Bearer ${token}` } },
      )

      if (fnError) throw fnError
      if (!fnData?.certificate_url) {
        throw new Error(fnData?.error || 'Certificate generation failed.')
      }

      setCertificateUrl(fnData.certificate_url)
      return fnData.certificate_url as string

    } catch (err: any) {
      const msg: string =
        err?.context?.json?.error ||
        err?.message ||
        'An unexpected error occurred generating your certificate.'
      setError(msg)
      return null
    } finally {
      setIsGenerating(false)
    }
  }, [supabase])

  const download = useCallback((url: string, filename = 'Still-Waters-Certificate.pdf') => {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setCertificateUrl(null)
  }, [])

  return { isGenerating, error, certificateUrl, generate, download, reset }
}
